import React, { useState, useEffect } from 'react';
import { DollarSign, Package, MessageSquare, Edit2, Save, X, Check, Clock, XCircle, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  new: 'bg-blue-500/20 text-blue-400',
  contacted: 'bg-yellow-500/20 text-yellow-400',
  negotiating: 'bg-purple-500/20 text-purple-400',
  converted: 'bg-green-500/20 text-green-400',
  declined: 'bg-red-500/20 text-red-400'
};

const SponsorshipManager = () => {
  const [packages, setPackages] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, converted: 0 });
  const [loading, setLoading] = useState(true);
  const [editingPackage, setEditingPackage] = useState(null);
  const [editForm, setEditForm] = useState({});
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeaders();
      const [packagesRes, inquiriesRes] = await Promise.all([
        axios.get(`${API}/api/sponsorship/admin/packages`, { headers }),
        axios.get(`${API}/api/sponsorship/admin/inquiries`, { headers })
      ]);
      
      setPackages(packagesRes.data.packages || []);
      setInquiries(inquiriesRes.data.inquiries || []);
      setStats(inquiriesRes.data.stats || { total: 0, new: 0, converted: 0 });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load sponsorship data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPackage = (pkg) => {
    setEditingPackage(pkg.package_id);
    setEditForm({
      name: pkg.name,
      price: pkg.price,
      duration: pkg.duration,
      features: pkg.features.join('\n'),
      highlight: pkg.highlight
    });
  };

  const handleSavePackage = async (packageId) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/api/sponsorship/admin/packages/${packageId}`, {
        name: editForm.name,
        price: parseFloat(editForm.price),
        duration: editForm.duration,
        features: editForm.features.split('\n').filter(f => f.trim()),
        highlight: editForm.highlight
      }, { headers });
      
      toast.success('Package updated');
      setEditingPackage(null);
      fetchData();
    } catch (error) {
      console.error('Failed to update package:', error);
      toast.error('Failed to update package');
    }
  };

  const handleUpdateInquiryStatus = async (inquiryId, status) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/api/sponsorship/admin/inquiries/${inquiryId}?status=${status}`, {}, { headers });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <DollarSign className="h-6 w-6 text-orange-500" />
            Sponsorship Management
          </h2>
          <p className="text-gray-400 mt-1">Manage packages and view inquiries</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 uppercase">Total Inquiries</p>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 uppercase">New (Action Needed)</p>
                <p className="text-2xl font-bold text-orange-500">{stats.new}</p>
              </div>
              <Clock className="h-8 w-8 text-orange-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 uppercase">Converted</p>
                <p className="text-2xl font-bold text-green-500">{stats.converted}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="inquiries" className="w-full">
        <TabsList className="bg-gray-800 border-gray-700">
          <TabsTrigger value="inquiries" className="data-[state=active]:bg-orange-500">
            <MessageSquare className="h-4 w-4 mr-2" />
            Inquiries ({inquiries.length})
          </TabsTrigger>
          <TabsTrigger value="packages" className="data-[state=active]:bg-orange-500">
            <Package className="h-4 w-4 mr-2" />
            Packages ({packages.length})
          </TabsTrigger>
        </TabsList>

        {/* Inquiries Tab */}
        <TabsContent value="inquiries" className="mt-4">
          {inquiries.length === 0 ? (
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-12 text-center">
                <MessageSquare className="h-12 w-12 mx-auto text-gray-700 mb-4" />
                <p className="text-gray-400">No inquiries yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {inquiries.map((inquiry) => (
                <Card key={inquiry.inquiry_id} className="bg-gray-900 border-gray-800">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-lg font-bold text-white">{inquiry.company_name}</h3>
                          <Badge className={statusColors[inquiry.status]}>
                            {inquiry.status}
                          </Badge>
                        </div>
                        <p className="text-gray-400 text-sm">
                          #{inquiry.inquiry_id} • {formatDate(inquiry.created_at)}
                        </p>
                      </div>
                      <Badge className="bg-orange-500/20 text-orange-400">
                        {inquiry.package_name}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Contact</p>
                        <p className="text-white text-sm">{inquiry.contact_name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Email</p>
                        <a href={`mailto:${inquiry.email}`} className="text-orange-400 text-sm hover:underline">
                          {inquiry.email}
                        </a>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Phone</p>
                        <p className="text-white text-sm">{inquiry.phone || 'Not provided'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Website</p>
                        {inquiry.website ? (
                          <a href={inquiry.website} target="_blank" rel="noopener noreferrer" className="text-orange-400 text-sm hover:underline">
                            Visit
                          </a>
                        ) : (
                          <p className="text-gray-500 text-sm">Not provided</p>
                        )}
                      </div>
                    </div>
                    
                    {inquiry.message && (
                      <div className="bg-gray-800 p-3 rounded-lg mb-4">
                        <p className="text-xs text-gray-500 uppercase mb-1">Message</p>
                        <p className="text-gray-300 text-sm">{inquiry.message}</p>
                      </div>
                    )}
                    
                    {inquiry.quiz_preferences?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs text-gray-500 uppercase mb-2">Quiz Preferences</p>
                        <div className="flex gap-2 flex-wrap">
                          {inquiry.quiz_preferences.map((quiz) => (
                            <Badge key={quiz} className="bg-gray-700 text-gray-300">{quiz}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2 pt-4 border-t border-gray-800">
                      <span className="text-sm text-gray-400 mr-2">Update Status:</span>
                      <Select
                        value={inquiry.status}
                        onValueChange={(value) => handleUpdateInquiryStatus(inquiry.inquiry_id, value)}
                      >
                        <SelectTrigger className="w-40 bg-gray-800 border-gray-700 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700">
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="negotiating">Negotiating</SelectItem>
                          <SelectItem value="converted">Converted</SelectItem>
                          <SelectItem value="declined">Declined</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Packages Tab */}
        <TabsContent value="packages" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            {packages.map((pkg) => (
              <Card key={pkg.package_id} className={`bg-gray-900 border-gray-800 ${pkg.highlight ? 'ring-2 ring-orange-500/50' : ''}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2">
                      {pkg.name}
                      {pkg.highlight && <Badge className="bg-orange-500 text-white text-xs">Popular</Badge>}
                    </CardTitle>
                    {editingPackage === pkg.package_id ? (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingPackage(null)}
                          className="text-gray-400"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleSavePackage(pkg.package_id)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Save className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditPackage(pkg)}
                        className="text-gray-400 hover:text-white"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {editingPackage === pkg.package_id ? (
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-gray-400">Name</label>
                        <Input
                          value={editForm.name}
                          onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                          className="bg-gray-800 border-gray-700 text-white"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-gray-400">Price ($)</label>
                          <Input
                            type="number"
                            value={editForm.price}
                            onChange={(e) => setEditForm(prev => ({ ...prev, price: e.target.value }))}
                            className="bg-gray-800 border-gray-700 text-white"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-400">Duration</label>
                          <Input
                            value={editForm.duration}
                            onChange={(e) => setEditForm(prev => ({ ...prev, duration: e.target.value }))}
                            className="bg-gray-800 border-gray-700 text-white"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-400">Features (one per line)</label>
                        <textarea
                          value={editForm.features}
                          onChange={(e) => setEditForm(prev => ({ ...prev, features: e.target.value }))}
                          className="w-full bg-gray-800 border border-gray-700 text-white rounded-md p-2 text-sm min-h-[120px]"
                        />
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editForm.highlight}
                          onChange={(e) => setEditForm(prev => ({ ...prev, highlight: e.target.checked }))}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-300">Highlight as "Popular"</span>
                      </label>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1 mb-4">
                        <span className="text-3xl font-bold text-white">${pkg.price}</span>
                        <span className="text-gray-500">/ {pkg.duration}</span>
                      </div>
                      <ul className="space-y-2">
                        {pkg.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                            <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SponsorshipManager;
